import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, startOfWeek, subDays, startOfMonth, endOfMonth, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { 
  X, Check, Wallet, AlertTriangle, 
  ArrowRight, ArrowLeft, Info, Calendar as CalendarIcon,
  Banknote, Lock, Clock, ClipboardList, ArrowDown, ArrowUp, Lightbulb, Calculator,
  Download, Upload
} from 'lucide-react';
import WizardDatePickerModal from './WizardDatePickerModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-DZ', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0) + ' DA';
};

const ClotureCaisseWizard = ({ onClose, onSuccess, headers, pharmacyIds, editData, initialDateRange }) => {
  const [step, setStep] = useState(editData ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  
  // Step 1: Période
  const [periodType, setPeriodType] = useState(() => {
    if (editData) return 'personnalisee';
    if (initialDateRange?.from && initialDateRange?.to) return 'personnalisee';
    return 'aujourdhui';
  });
  const [dateRange, setDateRange] = useState(() => {
    if (editData) {
      return { start: new Date(editData.period_start), end: new Date(editData.period_end) };
    }
    if (initialDateRange?.from && initialDateRange?.to) {
      const end = new Date(initialDateRange.to);
      end.setHours(23, 59, 59, 999);
      return { start: new Date(initialDateRange.from), end };
    }
    return { start: null, end: null };
  });
  const [currentState, setCurrentState] = useState(null);

  // Solde debut customizable
  const [customSoldeDebutStr, setCustomSoldeDebutStr] = useState(null);
  const [isEditingSoldeDebut, setIsEditingSoldeDebut] = useState(false);

  // Entrée mode: 'declare' | 'valide' | 'effectif'
  const [entreeMode, setEntreeMode] = useState('declare');

  // Step 2: Montant réel
  const [countedCash, setCountedCash] = useState('');

  // Step 3: Success data
  const [savedClosure, setSavedClosure] = useState(null);
  // INIT EDIT DATA
  useEffect(() => {
    if (editData) {
      setPeriodType('personnalisee');
      setDateRange({
        start: new Date(editData.period_start),
        end: new Date(editData.period_end)
      });
      setCountedCash(editData.montant_compte.toString());
      setCustomSoldeDebutStr(editData.solde_debut?.toString() || '0');
      setCurrentState({
        solde_debut: editData.solde_debut,
        total_entrees: editData.total_entrees,
        total_sorties: editData.total_sorties,
        solde_theorique: editData.solde_theorique
      });
      setStep(2); // Start at step 2 to prevent changing the period
    }
  }, [editData]);


  // Initialize date range based on selection
  useEffect(() => {
    if (editData) return;
    const now = new Date();
    let start, end;

    switch (periodType) {
      case 'aujourdhui':
        start = startOfDay(now);
        end = now;
        break;
      case 'cette_semaine':
        start = subDays(now, 7);
        end = now;
        break;
      case '2_semaines':
        start = subDays(now, 14);
        end = now;
        break;
      case 'ce_mois':
        start = startOfMonth(now);
        end = now;
        break;
      case 'mois_dernier':
        const prevMonth = subDays(startOfMonth(now), 1);
        start = startOfMonth(prevMonth);
        end = endOfMonth(prevMonth);
        break;
      case 'personnalisee':
        if (!dateRange.start) {
          if (initialDateRange?.from && initialDateRange?.to) {
            const endD = new Date(initialDateRange.to);
            endD.setHours(23, 59, 59, 999);
            start = new Date(initialDateRange.from);
            end = endD;
          } else {
            start = startOfDay(now);
            end = now;
          }
        } else {
          return;
        }
        break;
      default:
        start = startOfDay(now);
        end = now;
    }
    setDateRange({ start, end });
  }, [periodType]);

  // Fetch current state whenever date range changes
  useEffect(() => {
    if (dateRange.start && dateRange.end && step === 1) {
      fetchCurrentState(dateRange.start, dateRange.end);
    }
  }, [dateRange, step]);

  const fetchCurrentState = async (start, end) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/periodic-closures/current-state`, {
        params: {
          pharmacy_ids: pharmacyIds,
          start_date: start.toISOString(),
          end_date: end.toISOString()
        },
        headers
      });
      setCurrentState(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du calcul de la période');
    } finally {
      setLoading(false);
    }
  };

  const formatInputNumber = (val) => {
    if (val === undefined || val === null) return '';
    const stringVal = val.toString();
    // Keep only digits and decimal point
    const cleanValue = stringVal.replace(/[^\d.]/g, '');
    if (!cleanValue) return '';
    const parts = cleanValue.split('.');
    // Format integer part with spaces
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    // Limit to 1 decimal point
    return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : parts.join('.');
  };

  const handleCashChange = (e) => {
    setCountedCash(formatInputNumber(e.target.value));
  };

  const getRawNumber = (formattedStr) => {
    if (!formattedStr) return 0;
    const cleanStr = formattedStr.toString().replace(/\s/g, '');
    return parseFloat(cleanStr) || 0;
  };

  // Submissions
  const handleNext = () => {
    if (step === 2) {
      if (countedCash === '' || getRawNumber(countedCash) < 0) {
        toast.error('Veuillez saisir un montant valide');
        return;
      }
    }
    setStep(prev => prev + 1);
  };

  // Computed
  const countedAmount = getRawNumber(countedCash);
  const soldeDebut = customSoldeDebutStr !== null 
    ? (getRawNumber(customSoldeDebutStr)) 
    : (currentState?.solde_debut ?? 0);

  const totalEntrees = (() => {
    if (entreeMode === 'declare') {
      return currentState?.total_entrees_declare ?? currentState?.total_entrees ?? 0;
    }
    if (entreeMode === 'valide') {
      return currentState?.total_entrees_valide ?? currentState?.total_entrees ?? 0;
    }
    return currentState?.total_entrees ?? 0;
  })();

  const totalSorties = currentState?.total_sorties || 0;
  const theoriqueAmount = soldeDebut + totalEntrees - totalSorties;
  const ecartAmount = countedAmount - theoriqueAmount;

  const submitClosure = async () => {
    try {
      setLoading(true);

      const payload = {
        period_start: dateRange.start.toISOString(),
        period_end: dateRange.end.toISOString(),
        solde_debut: soldeDebut,
        total_entrees: totalEntrees,
        total_sorties: totalSorties,
        solde_theorique: theoriqueAmount,
        montant_compte: countedAmount,
        ecart: ecartAmount,
        motif_ecart: null,
        notes: '',
        pharmacy_id: pharmacyIds
      };
      let res;
      if (editData) {
        res = await axios.put(`${API}/periodic-closures/${editData.id}`, payload, { headers });
      } else {
        res = await axios.post(`${API}/periodic-closures`, payload, { headers });
      }
      
      setSavedClosure(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la clôture');
    } finally {
      setLoading(false);
    }
  };

  // Renderers for each step
  const renderStepIndicator = () => {
    const steps = [
      { id: 1, title: 'Période', name: 'Période de clôture' },
      { id: 2, title: 'Montant réel', name: 'Montant réel en caisse' },
      { id: 3, title: 'Récapitulatif', name: 'Récapitulatif & validation' }
    ];
    
    const currentStep = steps.find(s => s.id === step);
    
    return (
      <div className="border-b border-gray-100 px-8 py-6 bg-white relative">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{editData ? "Modification de la clôture" : "Nouvelle clôture de caisse"}</h2>
            <p className="text-sm font-semibold text-[#5B21B6] mt-1">Étape {step} sur 3 - {currentStep?.name}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="flex items-start justify-between max-w-lg mx-auto relative z-10">
          {steps.map((s, i) => {
            const isActive = step === s.id;
            const isPast = step > s.id;
            return (
              <div key={s.id} className="flex flex-col items-center gap-2 relative flex-1">
                {/* Connecting Line */}
                {i < steps.length - 1 && (
                  <div className={`absolute top-5 left-[50%] right-[-50%] h-[2px] -z-10 ${
                    isPast ? 'bg-[#5B21B6]' : 'bg-gray-200'
                  }`} />
                )}
                
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors bg-white ${
                  isActive || isPast ? 'bg-[#5B21B6] text-white' : 'border-2 border-gray-200 text-gray-400'
                }`}>
                  {isPast ? <Check size={18} /> : s.id}
                </div>
                
                <div className="text-center mt-1">
                  <p className={`text-xs font-bold ${isActive || isPast ? 'text-[#5B21B6]' : 'text-gray-400'}`}>{s.title}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getSubLabel = (id) => {
    const today = new Date();
    switch (id) {
      case 'aujourdhui': return format(today, 'dd MMM yyyy', { locale: fr });
      case 'cette_semaine': return `${format(subDays(today, 7), 'dd')} - ${format(today, 'dd MMM yyyy', { locale: fr })}`;
      case '2_semaines': return `${format(subDays(today, 14), 'dd')} - ${format(today, 'dd MMM yyyy', { locale: fr })}`;
      case 'ce_mois': return format(today, 'MMMM yyyy', { locale: fr });
      case 'mois_dernier': return format(subDays(startOfMonth(today), 1), 'MMMM yyyy', { locale: fr });
      case 'personnalisee': 
        if (periodType === 'personnalisee' && dateRange.start && dateRange.end) {
          if (dateRange.start.getTime() === dateRange.end.getTime()) {
            return format(dateRange.start, 'dd MMM yyyy', { locale: fr });
          }
          return `${format(dateRange.start, 'dd MMM', { locale: fr })} – ${format(dateRange.end, 'dd MMM yyyy', { locale: fr })}`;
        }
        return 'Choisir une période';
      default: return '';
    }
  };

  const renderStep1 = () => (
    <div className="p-8 space-y-8">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-purple-50 rounded-xl text-[#5B21B6]">
          <CalendarIcon size={24} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Sélectionnez la période à clôturer</h2>
          <p className="text-sm text-gray-500 mt-1">Choisissez la période pour laquelle vous souhaitez effectuer la clôture de caisse.</p>
        </div>
      </div>

      <div className="space-y-4">
        <p className="text-sm font-bold text-gray-800">Périodes rapides</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { id: 'aujourdhui', label: "Aujourd'hui", icon: CalendarIcon },
            { id: 'cette_semaine', label: 'Cette semaine', icon: CalendarIcon },
            { id: '2_semaines', label: '2 semaines', icon: CalendarIcon },
            { id: 'ce_mois', label: 'Ce mois', icon: CalendarIcon },
            { id: 'mois_dernier', label: 'Mois dernier', icon: CalendarIcon },
            { id: 'personnalisee', label: 'Personnalisée', icon: CalendarIcon }
          ].map(p => {
            const Icon = p.icon;
            return (
              <div 
                key={p.id}
                onClick={() => {
                  setPeriodType(p.id);
                  if (p.id === 'personnalisee') setIsDatePickerOpen(true);
                }}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-1 ${
                  periodType === p.id ? 'border-[#5B21B6] bg-purple-50/20 shadow-sm' : 'border-gray-100 hover:border-purple-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon size={16} className={periodType === p.id ? 'text-[#5B21B6]' : 'text-gray-400'} strokeWidth={2.5} />
                  <span className={`text-sm font-bold ${periodType === p.id ? 'text-[#5B21B6]' : 'text-gray-900'}`}>{p.label}</span>
                </div>
                <div className={`text-xs ml-6 ${periodType === p.id ? 'text-purple-600' : 'text-gray-500'}`}>{getSubLabel(p.id)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <WizardDatePickerModal 
        isOpen={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        initialRange={{ start: dateRange.start, end: dateRange.end }}
        onConfirm={(range) => {
          setDateRange({ start: range.start, end: range.end });
          setIsDatePickerOpen(false);
        }}
      />

      {dateRange.start && dateRange.end && (
        <div className="space-y-6">
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Période sélectionnée</p>
            <div className="bg-purple-50/50 rounded-xl p-4 flex flex-col md:flex-row items-center gap-6 justify-between border border-purple-50">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">Du</p>
                  <p className="text-sm font-bold text-gray-900">{format(dateRange.start, 'dd MMM yyyy', { locale: fr })}</p>
                </div>
                <ArrowRight size={16} className="text-gray-400 mt-4" />
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">Au</p>
                  <p className="text-sm font-bold text-gray-900">{format(dateRange.end, 'dd MMM yyyy', { locale: fr })}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <Clock size={16} />
                <span className="text-sm font-bold text-gray-600">Durée {Math.ceil((dateRange.end - dateRange.start) / (1000 * 60 * 60 * 24)) || 1} jours</span>
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Résumé de la période</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Card 1: Solde d'ouverture */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 text-blue-500">
                      <Wallet className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-0.5">Solde d'ouverture</p>
                      {!isEditingSoldeDebut ? (
                        <h3 className="text-lg font-bold text-gray-900">{formatCurrency(soldeDebut)}</h3>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="text"
                            value={customSoldeDebutStr !== null ? customSoldeDebutStr : soldeDebut.toString()}
                            onChange={(e) => setCustomSoldeDebutStr(formatInputNumber(e.target.value))}
                            className="w-32 px-2 py-1 border border-blue-400 rounded-lg text-sm font-bold focus:outline-none"
                            placeholder="0"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => setIsEditingSoldeDebut(false)}
                            className="text-xs bg-blue-600 text-white px-2 py-1 rounded font-semibold"
                          >
                            OK
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {!isEditingSoldeDebut && (
                    <div className="flex items-center gap-1">
                      {soldeDebut !== 0 && (
                        <button
                          type="button"
                          onClick={() => setCustomSoldeDebutStr('0')}
                          className="text-[11px] font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2 py-1 rounded-md transition-colors"
                          title="Mettre à 0 DA"
                        >
                          Mettre à 0
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setIsEditingSoldeDebut(true)}
                        className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-md transition-colors"
                      >
                        Modifier
                      </button>
                    </div>
                  )}
                </div>
                {currentState?.last_closure_end ? (
                  <p className="text-[11px] text-gray-400 mt-1">
                    Dernière clôture : {format(new Date(currentState.last_closure_end), 'dd/MM/yyyy', { locale: fr })}
                  </p>
                ) : (
                  <p className="text-[11px] text-gray-400 mt-1">Aucune clôture précédente</p>
                )}
              </div>

              {/* Card 2: Entrées cash */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0 text-emerald-500">
                      <Download className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-0.5">Entrées cash</p>
                      <h3 className="text-lg font-bold text-emerald-600">+{formatCurrency(totalEntrees)}</h3>
                    </div>
                  </div>
                </div>
                {currentState?.total_entrees_declare !== undefined && currentState?.total_entrees_valide !== undefined && (
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-50">
                    <button
                      type="button"
                      onClick={() => setEntreeMode('declare')}
                      className={`text-[11px] px-2 py-1 rounded-md transition-colors ${
                        entreeMode === 'declare'
                          ? 'bg-emerald-100 text-emerald-800 font-bold shadow-xs'
                          : 'bg-gray-50 text-gray-500 hover:bg-gray-100 font-medium'
                      }`}
                      title="Total déclaré par les caissiers"
                    >
                      Déclaré: {formatCurrency(currentState.total_entrees_declare)}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEntreeMode('valide')}
                      className={`text-[11px] px-2 py-1 rounded-md transition-colors ${
                        entreeMode === 'valide'
                          ? 'bg-purple-100 text-purple-800 font-bold shadow-xs'
                          : 'bg-gray-50 text-gray-500 hover:bg-gray-100 font-medium'
                      }`}
                      title="Total validé par les admins"
                    >
                      Validé: {formatCurrency(currentState.total_entrees_valide)}
                    </button>
                  </div>
                )}
              </div>

              {/* Card 3: Sorties cash */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0 text-red-500">
                      <Upload className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-0.5">Sorties cash</p>
                      <h3 className="text-lg font-bold text-red-600">-{formatCurrency(totalSorties)}</h3>
                    </div>
                  </div>
                </div>
                {currentState?.sorties_breakdown && (
                  <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-gray-50 text-[11px] text-gray-500">
                    <span>Achats: <b className="text-gray-700">{formatCurrency(currentState.sorties_breakdown.achats).replace(' DA','')}</b></span>
                    <span>•</span>
                    <span>Charges: <b className="text-gray-700">{formatCurrency(currentState.sorties_breakdown.charges).replace(' DA','')}</b></span>
                    <span>•</span>
                    <span>Salaires: <b className="text-gray-700">{formatCurrency(currentState.sorties_breakdown.salaires).replace(' DA','')}</b></span>
                  </div>
                )}
              </div>

              {/* Card 4: Solde théorique */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col justify-between">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0 text-purple-500">
                      <Calculator className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-0.5">Solde théorique</p>
                      <h3 className="text-lg font-bold text-gray-900">{formatCurrency(theoriqueAmount)}</h3>
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 pt-2 border-t border-gray-50">
                  {formatCurrency(soldeDebut).replace(' DA','')} + {formatCurrency(totalEntrees).replace(' DA','')} - {formatCurrency(totalSorties).replace(' DA','')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-4 border-t border-gray-50">
        <button 
          onClick={handleNext}
          disabled={loading || !currentState}
          className="bg-[#5B21B6] hover:bg-[#4C1D95] text-white px-8 py-3.5 rounded-xl font-bold flex items-center gap-2 transition-colors disabled:opacity-50"
        >
          {loading ? 'Chargement...' : 'Étape suivante'} <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="p-8 space-y-8 flex flex-col h-full min-h-[500px]">
      <div className="bg-purple-50 p-4 rounded-xl flex items-start gap-3">
        <Info className="text-[#5B21B6] flex-shrink-0 mt-0.5" size={18} />
        <p className="text-sm font-medium text-purple-900">
          Entrez le montant réel que vous avez actuellement en caisse à la fin de la période sélectionnée.
        </p>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center space-y-6 max-w-md mx-auto w-full">
        <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center text-[#5B21B6]">
          <Banknote size={32} />
        </div>
        
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Montant réel en caisse</h2>
          <p className="text-sm text-gray-500 mt-1">Saisissez le montant total compté en caisse.</p>
        </div>

        <div className="w-full relative mt-4">
          <input 
            type="text" 
            value={countedCash}
            onChange={handleCashChange}
            className="w-full text-center text-4xl sm:text-5xl font-black text-gray-900 py-6 px-16 rounded-2xl border-2 border-gray-200 focus:border-[#5B21B6] focus:ring-0 transition-colors bg-white shadow-sm"
            placeholder="0"
            autoFocus
          />
          <span className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-xl sm:text-2xl">
            DA
          </span>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-100 p-4 rounded-xl flex items-start gap-3 mt-auto mb-4">
        <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center flex-shrink-0 text-[#5B21B6] shadow-sm border border-gray-100">
          <Lightbulb size={14} />
        </div>
        <div>
          <p className="text-sm font-bold text-gray-700">Conseil</p>
          <p className="text-xs text-gray-500 mt-0.5">Comptez tout l'argent (billets et pièces) et saisissez le total sans détailler.</p>
        </div>
      </div>

      <div className="flex justify-between pt-4 border-t border-gray-50">
        {!editData ? (
          <button onClick={() => setStep(1)} className="px-6 py-3.5 text-sm font-semibold text-gray-500 hover:text-gray-800 flex items-center gap-2">
            <ArrowLeft size={16} /> Étape précédente
          </button>
        ) : (
          <div />
        )}
        <button onClick={handleNext} className="bg-[#5B21B6] hover:bg-[#4C1D95] text-white px-8 py-3.5 rounded-xl font-bold flex items-center gap-2 transition-colors shadow-sm">
          Étape suivante <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="p-8 space-y-8">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-purple-50 rounded-xl text-[#5B21B6]">
          <ClipboardList size={24} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Récapitulatif de la clôture</h2>
          <p className="text-sm text-gray-500 mt-1">Vérifiez les informations avant de valider définitivement.</p>
        </div>
      </div>

      <div className="space-y-1 bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
        <div className="flex justify-between py-3 border-b border-gray-50">
          <span className="text-sm font-medium flex items-center gap-3 text-gray-600"><CalendarIcon size={16} className="text-gray-400"/> Période clôturée</span>
          <span className="text-sm font-bold text-gray-900">{format(dateRange.start, 'dd MMM yyyy', {locale: fr})} → {format(dateRange.end, 'dd MMM yyyy', {locale: fr})}</span>
        </div>
        <div className="flex justify-between py-3 border-b border-gray-50">
          <span className="text-sm font-medium flex items-center gap-3 text-gray-600"><Wallet size={16} className="text-gray-400"/> Solde d'ouverture</span>
          <span className="text-sm font-bold text-gray-900">{formatCurrency(soldeDebut)}</span>
        </div>
        <div className="flex justify-between py-3 border-b border-gray-50">
          <span className="text-sm font-medium flex items-center gap-3 text-gray-600">
            <ArrowDown size={16} className="text-emerald-500"/> Total des entrées {entreeMode === 'declare' ? '(déclaré)' : '(validé)'}
          </span>
          <span className="text-sm font-bold text-emerald-600">+{formatCurrency(totalEntrees)}</span>
        </div>
        <div className="flex justify-between py-3 border-b border-gray-50">
          <span className="text-sm font-medium flex items-center gap-3 text-gray-600"><ArrowUp size={16} className="text-gray-900"/> Total des sorties</span>
          <span className="text-sm font-bold text-gray-900">-{formatCurrency(totalSorties)}</span>
        </div>
        <div className="flex justify-between pt-4 pb-2">
          <span className="text-sm font-medium flex items-center gap-3 text-gray-600"><Calculator size={16} className="text-gray-400"/> Solde théorique (attendu)</span>
          <span className="text-base font-black text-gray-900">{formatCurrency(theoriqueAmount)}</span>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-purple-50 rounded-xl p-5 flex justify-between items-center border border-purple-100">
          <span className="text-sm font-bold text-[#5B21B6] flex items-center gap-3">
            <div className="p-1.5 bg-white rounded-md shadow-sm text-[#5B21B6]"><Banknote size={16}/></div> 
            Montant réel en caisse
          </span>
          <span className="text-xl font-black text-[#5B21B6]">{formatCurrency(countedAmount)}</span>
        </div>

        {ecartAmount !== 0 && (
          <div className={`rounded-xl p-5 flex flex-col gap-2 border ${ecartAmount < 0 ? 'bg-pink-50/50 border-pink-100' : 'bg-emerald-50 border-emerald-100'}`}>
            <div className="flex justify-between items-center">
              <span className={`text-sm font-bold flex items-center gap-3 ${ecartAmount < 0 ? 'text-gray-900' : 'text-emerald-900'}`}>
                <AlertTriangle size={18} className={ecartAmount < 0 ? "text-pink-600" : "text-emerald-600"}/> Écart (Réel - Théorique)
              </span>
              <div className="flex items-center gap-3 flex-col sm:flex-row">
                <span className={`text-xl font-black ${ecartAmount < 0 ? 'text-[#BE185D]' : 'text-[#10B981]'}`}>
                  {ecartAmount > 0 ? '+' : ''}{formatCurrency(ecartAmount)} 
                </span>
                <span className={`text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider flex items-center gap-1 ${ecartAmount < 0 ? 'bg-pink-100 text-pink-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {ecartAmount < 0 ? <><ArrowDown size={12}/> Déficit</> : <><ArrowUp size={12}/> Surplus</>}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-purple-50/50 border border-purple-100 p-4 rounded-xl flex items-start gap-3">
        <AlertTriangle className="text-[#5B21B6] flex-shrink-0 mt-0.5" size={18} />
        <p className="text-xs font-medium text-[#5B21B6]">
          Après validation, cette période sera considérée comme clôturée. Les mouvements de cette période resteront consultables mais vous ne pourrez plus les modifier.
        </p>
      </div>

      <div className="flex justify-between pt-4 border-t border-gray-50">
        <button onClick={() => setStep(2)} className="px-6 py-3.5 text-sm font-semibold text-gray-500 hover:text-gray-800 flex items-center gap-2">
          <ArrowLeft size={16} /> Étape précédente
        </button>
        <button 
          onClick={submitClosure}
          disabled={loading}
          className="bg-[#5B21B6] hover:bg-[#4C1D95] text-white px-8 py-3.5 rounded-xl font-bold flex items-center gap-2 transition-colors disabled:opacity-50 shadow-sm"
        >
          {loading ? 'Traitement...' : 'Valider la clôture'} <Lock size={16} className="ml-1" />
        </button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="p-12 flex flex-col items-center justify-center space-y-6 text-center">
      <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
        <Check className="w-12 h-12 text-emerald-600" />
      </div>
      <h2 className="text-3xl font-black text-gray-900">Clôture validée !</h2>
      <span className="px-4 py-1.5 bg-gray-100 rounded-full text-sm font-bold text-gray-600">
        Clôture #{savedClosure.id.split('-')[0].toUpperCase()}
      </span>
      <p className="text-gray-600 font-medium text-lg">
        {format(dateRange.start, 'dd MMM yyyy', { locale: fr })} → {format(dateRange.end, 'dd MMM yyyy', { locale: fr })}
      </p>
      
      <div className="w-full flex justify-center mt-8">
        <button onClick={onSuccess} className="px-12 py-3.5 rounded-xl font-bold text-white bg-[#5B21B6] hover:bg-[#4C1D95] transition-colors shadow-sm">
          Retour au tableau de bord
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-gray-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[600px] max-h-[95vh] overflow-y-auto flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Step Indicator */}
        {!savedClosure && renderStepIndicator()}

        {/* Content */}
        <div className="flex-1 relative">
          {savedClosure ? renderSuccess() : (
            <>
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClotureCaisseWizard;
